import { useState, useEffect } from 'react';
import { X, Check, Loader2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { updateMealItem } from '@/hooks/useMeals';
import { useTelegram } from '@/hooks/useTelegram';
import { useBodyClass } from '@/hooks/useBodyClass';
import { ComponentsEditor } from '@/components/ComponentsEditor';
import { backfillComponentMacros } from '@/lib/componentMacros';
import type { MealComponent } from '@/types';

// ════════════════════════════════════════════════════════════════
// EditItemModal v2 — теперь использует общий ComponentsEditor
//
// Изменения:
// 1. Удалён локальный ComponentEditor (был отдельной карточкой)
// 2. Используется общий <ComponentsEditor> как в AddMeal
// 3. При изменении components — итоги КБЖУ блюда пересчитываются МГНОВЕННО
//    (без confirm — это привычное поведение из AddMeal)
// 4. Свободная форма веса + AI пересчёт работают из коробки
// ════════════════════════════════════════════════════════════════

interface MealItem {
  id: string;
  name: string;
  weight_g: number;
  calories: number;
  protein_g: number;
  fat_g: number;
  carbs_g: number;
  components?: MealComponent[];
}

interface Props {
  item: MealItem | null;
  onClose: () => void;
  onSaved: () => void;
}

export function EditItemModal({ item, onClose, onSaved }: Props) {
  const { haptic } = useTelegram();

  // Скрываем BottomNav пока модалка открыта
  useBodyClass('modal-open', !!item);

  const [name, setName] = useState('');
  const [weight, setWeight] = useState('');
  const [calories, setCalories] = useState('');
  const [protein, setProtein] = useState('');
  const [fat, setFat] = useState('');
  const [carbs, setCarbs] = useState('');
  const [saving, setSaving] = useState(false);
  const [autoCalc, setAutoCalc] = useState(true);
  const [origWeight, setOrigWeight] = useState(0);
  const [origCalories, setOrigCalories] = useState(0);
  const [origProtein, setOrigProtein] = useState(0);
  const [origFat, setOrigFat] = useState(0);
  const [origCarbs, setOrigCarbs] = useState(0);

  // 🆕 v2: components теперь как обычный массив, без локального draft
  const [components, setComponents] = useState<MealComponent[]>([]);

  useEffect(() => {
    if (item) {
      setName(item.name);
      setWeight(String(item.weight_g));
      setCalories(String(item.calories));
      setProtein(String(item.protein_g));
      setFat(String(item.fat_g));
      setCarbs(String(item.carbs_g));
      setOrigWeight(item.weight_g);
      setOrigCalories(item.calories);
      setOrigProtein(Number(item.protein_g));
      setOrigFat(Number(item.fat_g));
      setOrigCarbs(Number(item.carbs_g));
      setAutoCalc(true);
      // Загружаем components если есть. Раскладываем макросы блюда по компонентам,
      // если у них их нет — иначе правка состава обнулит Б/Ж/У блюда.
      setComponents(
        backfillComponentMacros(
          item.components && Array.isArray(item.components) ? item.components : [],
          {
            protein_g: Number(item.protein_g) || 0,
            fat_g: Number(item.fat_g) || 0,
            carbs_g: Number(item.carbs_g) || 0,
          }
        )
      );
    }
  }, [item]);

  function handleWeightChange(newWeight: string) {
    setWeight(newWeight);
    if (autoCalc && origWeight > 0) {
      const w = Number(newWeight);
      if (w && w > 0) {
        const ratio = w / origWeight;
        setCalories(String(Math.round(origCalories * ratio)));
        setProtein((origProtein * ratio).toFixed(1));
        setFat((origFat * ratio).toFixed(1));
        setCarbs((origCarbs * ratio).toFixed(1));
      }
    }
  }

  function handleManualEdit(field: 'calories' | 'protein' | 'fat' | 'carbs', value: string) {
    setAutoCalc(false);
    if (field === 'calories') setCalories(value);
    if (field === 'protein') setProtein(value);
    if (field === 'fat') setFat(value);
    if (field === 'carbs') setCarbs(value);
  }

  // 🆕 v2: обработчик изменения components — мгновенно пересчитывает КБЖУ блюда
  function handleComponentsChange(
    newComponents: MealComponent[],
    recalcTotals: {
      weight_g: number;
      calories: number;
      protein_g: number;
      fat_g: number;
      carbs_g: number;
    }
  ) {
    setComponents(newComponents);
    // 🆕 Мгновенно обновляем КБЖУ блюда — как в AddMeal
    setWeight(String(recalcTotals.weight_g));
    setCalories(String(recalcTotals.calories));
    setProtein(recalcTotals.protein_g.toFixed(1));
    setFat(recalcTotals.fat_g.toFixed(1));
    setCarbs(recalcTotals.carbs_g.toFixed(1));
    setAutoCalc(false); // отключаем авто-пересчёт от веса т.к. цифры из components
  }

  async function save() {
    if (!item) return;
    const w = Number(weight);
    const cal = Number(calories);
    const p = Number(protein);
    const f = Number(fat);
    const c = Number(carbs);

    if (!name.trim()) {
      alert('Название не может быть пустым');
      return;
    }
    if (!w || w < 1 || w > 5000) {
      alert('Вес должен быть от 1 до 5000 г');
      return;
    }
    if (cal < 0 || p < 0 || f < 0 || c < 0) {
      alert('Значения не могут быть отрицательными');
      return;
    }

    setSaving(true);
    haptic('medium');
    try {
      await updateMealItem(item.id, {
        name: name.trim(),
        weight_g: Math.round(w),
        calories: Math.round(cal),
        protein_g: Number(p.toFixed(1)),
        fat_g: Number(f.toFixed(1)),
        carbs_g: Number(c.toFixed(1)),
        components: components,
      } as any);
      haptic('success');
      onSaved();
      onClose();
    } catch (e: any) {
      console.error(e);
      alert('Не удалось сохранить: ' + (e.message || 'неизвестная ошибка'));
      haptic('error');
    } finally {
      setSaving(false);
    }
  }

  const canSave = !saving && !!name.trim() && !!weight;

  return (
    <AnimatePresence>
      {item && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/70 z-40 backdrop-blur-sm"
          />

          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            className="fixed bottom-0 left-0 right-0 z-50 bg-bg-card rounded-t-3xl border-t border-line max-w-md mx-auto flex flex-col"
            style={{
              maxHeight: '90dvh',
            }}
          >
            {/* Header */}
            <div className="bg-bg-card border-b border-line/40 px-5 py-3 flex items-center justify-between shrink-0">
              <div className="font-display font-bold text-base">Редактировать продукт</div>
              <button
                onClick={onClose}
                className="w-8 h-8 rounded-full bg-bg-elevated text-ink-muted flex items-center justify-center"
              >
                <X size={16} />
              </button>
            </div>

            {/* Контент */}
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
              <div>
                <label className="text-[11px] text-ink-dim block mb-1">Название</label>
                <input
                  type="text"
                  className="input text-sm"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={saving}
                  maxLength={100}
                />
              </div>
              <div>
                <label className="text-[11px] text-ink-dim mb-1 flex items-center justify-between">
                  <span>Вес, г</span>
                  {autoCalc && components.length === 0 && (
                    <span className="text-[10px] text-emerald-glow">
                      ✨ КБЖУ пересчитывается
                    </span>
                  )}
                </label>
                <input
                  type="number"
                  inputMode="decimal"
                  className="input text-sm"
                  value={weight}
                  onChange={(e) => handleWeightChange(e.target.value)}
                  disabled={saving}
                  min={1}
                  max={5000}
                />
              </div>
              <div className="border-t border-line/40 pt-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[11px] text-ink-dim">КБЖУ для этого веса</span>
                  {!autoCalc && components.length === 0 && (
                    <button
                      onClick={() => {
                        setAutoCalc(true);
                        handleWeightChange(weight);
                      }}
                      className="text-[10px] text-emerald-glow hover:underline"
                    >
                      ↻ Пересчитать автоматически
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-[10px] text-ink-dim block mb-1">Калории</label>
                    <input
                      type="number"
                      inputMode="numeric"
                      className="input text-sm py-2"
                      value={calories}
                      onChange={(e) => handleManualEdit('calories', e.target.value)}
                      disabled={saving}
                    />
                  </div>
                  <div>
                    <label className="text-[10px] text-emerald-glow block mb-1">Белки, г</label>
                    <input
                      type="number"
                      inputMode="decimal"
                      step="0.1"
                      className="input text-sm py-2"
                      value={protein}
                      onChange={(e) => handleManualEdit('protein', e.target.value)}
                      disabled={saving}
                    />
                  </div>
                  <div>
                    <label className="text-[10px] text-warn block mb-1">Жиры, г</label>
                    <input
                      type="number"
                      inputMode="decimal"
                      step="0.1"
                      className="input text-sm py-2"
                      value={fat}
                      onChange={(e) => handleManualEdit('fat', e.target.value)}
                      disabled={saving}
                    />
                  </div>
                  <div>
                    <label className="text-[10px] text-sky-glow block mb-1">Углеводы, г</label>
                    <input
                      type="number"
                      inputMode="decimal"
                      step="0.1"
                      className="input text-sm py-2"
                      value={carbs}
                      onChange={(e) => handleManualEdit('carbs', e.target.value)}
                      disabled={saving}
                    />
                  </div>
                </div>
              </div>

              {/* 🆕 v2: Используем общий ComponentsEditor — такой же как в AddMeal */}
              <ComponentsEditor
                components={components}
                onChange={handleComponentsChange}
              />

              <p className="text-[10px] text-ink-dim leading-relaxed">
                💡 Измени вес — КБЖУ пересчитаются автоматически. Если знаешь точные значения — введи их вручную.
              </p>
            </div>

            {/* STICKY FOOTER */}
            <div className="border-t border-line/40 px-5 py-3 bg-bg-card shrink-0">
              <div className="flex gap-2">
                <button
                  onClick={onClose}
                  disabled={saving}
                  className="flex-1 py-3 rounded-xl bg-bg-elevated text-sm text-ink-muted disabled:opacity-50"
                >
                  Отмена
                </button>
                <button
                  onClick={save}
                  disabled={!canSave}
                  className="flex-1 py-3 rounded-xl bg-emerald-glow text-bg text-sm font-bold flex items-center justify-center gap-1.5 disabled:opacity-50 active:scale-[0.98] transition-all"
                >
                  {saving ? (
                    <>
                      <Loader2 size={14} className="animate-spin" />
                      Сохраняем…
                    </>
                  ) : (
                    <>
                      <Check size={14} />
                      Сохранить
                    </>
                  )}
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
